import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import projects from "../../data/projects.json";
import stillSets from "../../data/stills.json";
import "./Home.css";

function Home() {
  // pick a representative item per category
  const pick = (cat) =>
    projects.find(
      (p) =>
        p.category === cat &&
        ((Array.isArray(p.frames) && p.frames.length >= 3) || p.thumbnailUrl)
    );

  const blocks = [
    { to: "/narratives", label: "Narratives", item: pick("Narratives") },
    { to: "/movies", label: "Commercials", item: pick("Commercials") },
    {
      to: "/music-videos",
      label: "Music Videos",
      item: pick("Music Videos") || pick("MusicVideos"),
    },
  ];

  // Stills triptych (first set, up to 3 images)
  const firstSet = stillSets.find(
    (s) => Array.isArray(s.images) && s.images.length > 0
  );
  const stills3 = (() => {
    if (!firstSet) return [];
    const imgs = firstSet.images.slice(0, 3);
    while (imgs.length < 3) imgs.push(imgs[imgs.length - 1]);
    return imgs;
  })();

  const blocksWithStills = [
    ...blocks,
    { to: "/stills", label: "Stills", triptych: stills3 },
  ];

  // ===== VISIBLE ENTRY ANIMATION (stagger on page load + on scroll) =====
  const containerRef = useRef(null);
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const tiles = Array.from(root.querySelectorAll(".Home__block"));

    // 1) Immediately stagger-in the tiles that are already in view
    tiles.forEach((el, i) => {
      const delay = i * 140; // bigger delay => more dramatic
      setTimeout(() => el.classList.add("is-in"), delay);
    });

    // 2) Also observe for any tiles that appear later (long pages)
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    tiles.forEach((el) => io.observe(el));

    return () => io.disconnect();
  }, []);

  return (
    <main className="Home" id="main">
      <section className="Home__sections" ref={containerRef}>
        {blocksWithStills.map((b, i) => (
          <Link
            key={b.label}
            to={b.to}
            className="Home__block"
            aria-label={b.label}
            style={{ "--stagger": i }} // used for sheen delay too
          >
            <div className="Home__blockMedia">
              {Array.isArray(b.item?.frames) && b.item.frames.length >= 3 ? (
                <div className="Home__triptych">
                  {b.item.frames.slice(0, 3).map((src, j) => (
                    <img
                      key={j}
                      className="Home__triptychImg"
                      src={src}
                      alt={`${b.label} frame ${j + 1}`}
                      loading="lazy"
                    />
                  ))}
                </div>
              ) : Array.isArray(b.triptych) && b.triptych.length ? (
                <div className="Home__triptych">
                  {b.triptych.map((src, j) => (
                    <img
                      key={j}
                      className="Home__triptychImg"
                      src={src}
                      alt={`Stills frame ${j + 1}`}
                      loading="lazy"
                    />
                  ))}
                </div>
              ) : (
                <img
                  className="Home__img"
                  src={b.item?.thumbnailUrl || ""}
                  alt={b.label}
                  loading="lazy"
                />
              )}

              <div className="Home__overlay">
                <div className="Home__overlayText">{b.label}</div>
              </div>
            </div>
          </Link>
        ))}
      </section>
    </main>
  );
}

export default Home;
